/**
 * Unified Project Management Component
 * Combines PM Schedules (intake/recurring jobs) with Project Command Center (Kanban/Table/Timeline)
 */

import { useState, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, FolderKanban, ClipboardList, HardHat } from "lucide-react";
import PMSchedulesManagement from "./PMSchedulesManagement";
import ProjectCommandCenter from "./ProjectCommandCenter";
import ProposalsPanel from "./ProposalsPanel";
import ConstructionProposalsPanel from "./ConstructionProposalsPanel";
import TrainingLauncher from "@/components/training/TrainingLauncher";
import { useAuth } from "@/App";

export default function UnifiedProjectManagement() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("projects");
  const [refreshKey, setRefreshKey] = useState(0);

  // Handle when a PM Schedule is converted to a Project
  const handleConvertToProject = useCallback((projectId) => {
    // Switch to Projects tab
    setActiveTab("projects");
    // Trigger refresh of projects list
    setRefreshKey(prev => prev + 1);
  }, []);

  return (
    <div className="space-y-6" data-testid="unified-project-management">
      {/* Header with unified title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FolderKanban className="w-7 h-7 text-blue-600" />
            Project Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage PM schedules and construction projects in one place
          </p>
        </div>
        <TrainingLauncher trackId="project-management" />
      </div>

      {/* Main Tabs */}
      <ProposalsPanel token={token} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-fit grid-cols-3 mb-6">
          <TabsTrigger 
            value="pm-schedules" 
            className="flex items-center gap-2 px-6"
            data-testid="tab-pm-schedules"
          >
            <Calendar className="w-4 h-4" />
            PM Schedules
          </TabsTrigger>
          <TabsTrigger 
            value="projects" 
            className="flex items-center gap-2 px-6"
            data-testid="tab-projects"
          >
            <ClipboardList className="w-4 h-4" />
            Projects
          </TabsTrigger>
          <TabsTrigger
            value="construction"
            className="flex items-center gap-2"
            data-testid="tab-construction-proposals"
          >
            <HardHat className="w-4 h-4" />
            Construction Proposals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pm-schedules" className="mt-0">
          <div className="pm-schedules-embedded">
            <PMSchedulesManagement 
              embedded={true} 
              onConvertToProject={handleConvertToProject}
            />
          </div>
        </TabsContent>

        <TabsContent value="construction" className="mt-0">
          <ConstructionProposalsPanel token={token} />
        </TabsContent>

        <TabsContent value="projects" className="mt-0">
          <div className="project-command-embedded">
            <ProjectCommandCenter 
              embedded={true} 
              key={refreshKey}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
